import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ConfigProvider } from 'antd';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RequestPasswordResetPage from './pages/RequestPasswordResetPage';
import ContactPage from './pages/ContactPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import FixedIncomeFlowHistoryPage from './pages/FixedIncomeFlowHistoryPage';
import Disclaimer from './components/Disclaimer';
import Layout from './components/Layout';
import './theme/main.css';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isTokenValidating, setIsTokenValidating] = useState(true);
  const [user, setUser] = useState(null);
  const [investmentType, setInvestmentType] = useState('crypto');
  const [availableTypes, setAvailableTypes] = useState(['legacy']);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // localStorage 안전한 저장 함수
  const safeSetItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.error('localStorage setItem failed:', error);
      try {
        sessionStorage.setItem(key, value);
      } catch (sessionError) {
        console.error('sessionStorage setItem also failed:', sessionError);
      }
    }
  };

  // localStorage 안전한 읽기 함수
  const safeGetItem = (key) => {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch (error) {
      console.error('localStorage getItem failed:', error);
      try {
        return sessionStorage.getItem(key);
      } catch (sessionError) {
        console.error('sessionStorage getItem also failed:', sessionError);
      }
    }
  };

  // localStorage 안전한 삭제 함수
  const safeRemoveItem = (key) => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('localStorage removeItem failed:', error);
      try {
        sessionStorage.removeItem(key);
      } catch (sessionError) {
        console.error('sessionStorage removeItem also failed:', sessionError);
      }
    }
  };

  // 토큰 갱신 함수
  const refreshToken = async () => {
    try {
      const token = safeGetItem('authToken');
      if (!token) return false;

      const response = await axios.post('/api/auth/refresh', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        safeSetItem('authToken', response.data.token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  // 주기적 토큰 갱신 (23시간마다)
  useEffect(() => {
    if (isAuthenticated) {
      const refreshInterval = setInterval(async () => {
        const success = await refreshToken();
        if (!success) {
          handleLogout();
        }
      }, 23 * 60 * 60 * 1000);

      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated]);

  // 토큰 검증 및 사용자 정보 가져오기
  useEffect(() => {
    const validateToken = async () => {
      try {
        const token = safeGetItem('authToken');
        if (!token) {
          setIsTokenValidating(false);
          return;
        }

        const response = await axios.get('/api/auth/validate', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setUser(response.data.user);
          setIsAuthenticated(true);
          
          // 사용자 타입에 따라 사용 가능한 투자 유형 설정
          if (response.data.user.type === 'admin') {
            setAvailableTypes(['legacy', 'fixed-income', 'preferred-return']);
          } else {
            setAvailableTypes(['legacy']);
          }
        } else {
          safeRemoveItem('authToken');
          safeRemoveItem('userData');
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        safeRemoveItem('authToken');
        safeRemoveItem('userData');
      } finally {
        setIsTokenValidating(false);
      }
    };

    validateToken();
  }, []);

  // 로그인 처리
  const handleLogin = async (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    safeSetItem('authToken', token);
    safeSetItem('userData', JSON.stringify(userData));
    
    // 사용자 타입에 따라 사용 가능한 투자 유형 설정
    if (userData.type === 'admin') {
      setAvailableTypes(['legacy', 'fixed-income', 'preferred-return']);
    } else {
      setAvailableTypes(['legacy']);
    }
  };

  // 로그아웃 처리
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setInvestmentType('crypto');
    setAvailableTypes(['legacy']);
    setIsPreviewMode(false);
    safeRemoveItem('authToken');
    safeRemoveItem('userData');
  };

  // 투자 유형 변경 처리
  const handleInvestmentTypeChange = (type) => {
    setInvestmentType(type);
  };

  // 사용 가능한 투자 유형 업데이트 처리
  const handleAvailableTypesChange = (types) => {
    setAvailableTypes(types);
  };

  // 관리자 권한 확인
  const isAdmin = user?.type === 'admin';

  // Ant Design 테마 설정
  const antdTheme = {
    token: {
      colorPrimary: '#04A1D3',
      colorText: '#ffffff',
      colorBgContainer: '#072A47',
      colorBgElevated: '#0A385F',
      colorBorder: '#0E3F5A',
      borderRadius: 10,
      fontFamily: 'Montserrat, sans-serif',
    },
  };

  if (isTokenValidating) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <ConfigProvider theme={antdTheme}>
      <div className="app">
        <Routes>
          {/* 공개 페이지들 (비로그인 사용자만 접근 가능) */}
          <Route
            path="/request-password-reset"
            element={
              isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <RequestPasswordResetPage />
            }
          />
          <Route 
            path="/reset-password" 
            element={
              isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <ResetPasswordPage />
            }
          />
          
          {/* 로그인 페이지 (비로그인 사용자만 접근 가능) */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <LoginPage onLogin={handleLogin} />
            }
          />
          
          {/* Contact 페이지 (공개 페이지, 모든 사용자 접근 가능) */}
          <Route
            path="/contact"
            element={
              <Layout 
                user={user}
                onLogout={handleLogout}
                investmentType={investmentType}
                onSelectInvestmentType={handleInvestmentTypeChange}
                availableTypes={availableTypes}
                isPreviewMode={isPreviewMode}
                isPublicPage={true}
              >
                <ContactPage user={user} />
              </Layout>
            }
          />
          
          {/* 보호된 페이지들 (로그인 필요) */}
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <Layout 
                  user={user}
                  onLogout={handleLogout}
                  investmentType={investmentType}
                  onSelectInvestmentType={handleInvestmentTypeChange}
                  availableTypes={availableTypes}
                  isPreviewMode={isPreviewMode}
                  isPublicPage={false}
                >
                  <Routes>
                    <Route 
                      path="/dashboard" 
                      element={<DashboardPage user={user} onLogout={handleLogout} onAvailableTypesChange={handleAvailableTypesChange} />} 
                    />
                    <Route 
                      path="/admin" 
                      element={
                        isAdmin ? 
                        <AdminPage user={user} onLogout={handleLogout} /> : 
                        <Navigate to="/dashboard" replace />
                      } 
                    />
                    <Route 
                      path="/account-settings" 
                      element={<AccountSettingsPage user={user} onLogout={handleLogout} />} 
                    />
                    <Route
                      path="/fixed-income-flow-history"
                      element={<FixedIncomeFlowHistoryPage user={user} />}
                    />
                    <Route 
                      path="/" 
                      element={
                        (() => {
                          const currentPath = window.location.pathname;
                          const currentSearch = window.location.search;
                          
                          if (currentPath === '/dashboard' && (currentSearch.includes('email=') || currentSearch.includes('hash_id='))) {
                            return <DashboardPage user={user} onLogout={handleLogout} />;
                          }
                          
                          return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
                        })()
                      } 
                    />
                  </Routes>
                </Layout>
              ) : (
                // 로그아웃된 사용자는 로그인 페이지로 리다이렉트
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </div>
    </ConfigProvider>
  );
}

export default App;
